import { datadogRum } from '@datadog/browser-rum';

import {
  ALERT_STATUS,
  ALERT_TYPE,
} from '~/lib/constants/providerDashboard.constants';
import urlConstants from '~/lib/constants/url.constants';
import {
  AlertType,
  ProviderAlert,
  UpdateAlertStatusRequest,
} from '~/lib/types/providerDashboard';
import { HttpRequestMethod } from '~/store';
import { CacheTag } from '~/store/services/CacheTag';

const KNOWN_ALERT_TYPES = new Set<string>(Object.values(ALERT_TYPE));

const normalizeAlertType = (alertType: string): AlertType =>
  KNOWN_ALERT_TYPES.has(alertType)
    ? (alertType as AlertType)
    : ALERT_TYPE.UNKNOWN;

import api from './api';

export const providerDashboardV2Api = api.injectEndpoints({
  endpoints: (build) => ({
    getProviderAlerts: build.query<ProviderAlert[], { providerId: string }>({
      providesTags: [CacheTag.ProviderDashboardV2],
      query: ({ providerId }) => ({
        url: urlConstants.providerDashboard.getAlerts(providerId),
      }),
      transformResponse: (response: ProviderAlert[]) =>
        response.map((alert) => ({
          ...alert,
          alertType: normalizeAlertType(alert.alertType),
        })),
    }),
    updateAlertStatus: build.mutation<void, UpdateAlertStatusRequest>({
      invalidatesTags: [CacheTag.ProviderDashboardV2],
      onQueryStarted: async (
        { providerId, alertId, status },
        { dispatch, queryFulfilled },
      ) => {
        if (
          status !== ALERT_STATUS.COMPLETED &&
          status !== ALERT_STATUS.DISMISSED
        ) {
          return;
        }
        const patchResult = dispatch(
          providerDashboardV2Api.util.updateQueryData(
            'getProviderAlerts',
            { providerId },
            (draft) => {
              const index = draft.findIndex((a) => a.alertId === alertId);
              if (index !== -1) draft.splice(index, 1);
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch (error) {
          patchResult.undo();
          datadogRum.addError(
            new Error('Failed to update alert status'),
            { alertId, status },
          );
        }
      },
      query: ({
        providerId,
        alertId,
        status,
        patientExternalId,
        dismissReason,
        dismissReasonFreetext,
      }) => ({
        method: HttpRequestMethod.PATCH,
        url: urlConstants.providerDashboard.updateAlertStatus(
          providerId,
          alertId,
        ),
        body: { status, patientExternalId, dismissReason, dismissReasonFreetext },
      }),
    }),
  }),
});

export const { useGetProviderAlertsQuery, useUpdateAlertStatusMutation } =
  providerDashboardV2Api;
